import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import esBg from '../../../images/departments/es.jpg';
import hbBg from '../../../images/departments/hb.jpg';
import mmBg from '../../../images/departments/mm.jpg';
import msBg from '../../../images/departments/ms.jpg';

const departmentData = [
  {
    id: 'es',
    title: 'Electrical Systems',
    description: 'The Electrical Systems department ensures the operational integrity of the vessel\'s power and control infrastructure. The team handles everything from custom PCB development and LTO battery integration to the deployment of autonomous navigation and telemetry systems. Their work guarantees that energy harvesting and power distribution remain safe, efficient, and reliable in maritime conditions.',
    subareas: ['Autonomous Systems', 'Control', 'Battery', 'Solar Panels'],
    image: esBg,
  },
  {
    id: 'hb',
    title: 'Hull & Body',
    description: 'The Hull & Body department focuses on the vessel\'s structural design and hydrodynamic efficiency. By integrating sustainable materials like flax and cork into high-performance bio-composites, the team engineers lightweight, durable architectures. This unit manages the entire lifecycle of the hull, from initial CFD simulations and mold machining to the final precision manufacturing.',
    subareas: ['Machining', 'Materials', 'Hull Design', 'Manufacturing'],
    image: hbBg,
  },
  {
    id: 'mm',
    title: 'Management & Marketing',
    description: 'The Management & Marketing department directs the project\'s strategic growth and global visibility. This unit secures vital sponsorship and funding, manages complex logistics for international competitions, and oversees resource allocation. By coordinating public relations and social media, they ensure the project maintains a professional brand and gains widespread institutional exposure.',
    subareas: ['Finance', 'Logistics', 'External Relations', 'Marketing'],
    image: mmBg,
  },
  {
    id: 'ms',
    title: 'Mechanical Systems',
    description: 'The Mechanical Systems department is responsible for the design, validation, and production of the prototype\'s kinetic components. The primary focus lies in optimizing propulsion columns and transmission systems to deliver maximum thrust. Additionally, the team develops advanced hydrofoil technology and automated steering mechanics to enhance stability and minimize water resistance.',
    subareas: ['Hydrofoils', 'Propulsion', 'Automation', 'Rudder Systems'],
    image: msBg,
  }
];

function DepartmentCard({ dept, index, isActive, onClick }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (cardRef.current) {
      gsap.fromTo(cardRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, delay: index * 0.1, ease: 'power3.out' }
      );
    }
  }, [index]);

  return (
    <button
      ref={cardRef}
      onClick={onClick}
      className="group relative w-full overflow-hidden rounded-2xl text-left transition-all duration-300 hover:shadow-lg hover:shadow-black/20 min-h-[200px]"
    >
      {!isActive && (
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
          style={{ backgroundImage: `url(${dept.image})` }}
        />
      )}
      <div className={`absolute inset-0 transition-all duration-300 ${isActive ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20'}`} />

      <div className="relative z-10 h-full flex flex-col justify-end p-5 md:p-6">
        <h3 className="text-white font-custom font-bold text-lg md:text-xl leading-tight mb-1">
          {dept.title}
        </h3>
        <div className="flex items-center gap-2 text-gray-400 text-xs group-hover:text-white transition-colors duration-300">
          <span>{dept.subareas.length} subareas</span>
          <svg className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>
      </div>
    </button>
  );
}

export default function DepartmentSection() {
  const [activeIndex, setActiveIndex] = useState(-1);
  const sectionRef = useRef(null);
  const detailRef = useRef(null);

  useEffect(() => {
    if (sectionRef.current) {
      gsap.fromTo(sectionRef.current.querySelector('.section-header'),
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }
      );
    }
  }, []);

  useEffect(() => {
    if (activeIndex >= 0 && detailRef.current) {
      gsap.fromTo(detailRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power3.out' }
      );
    }
  }, [activeIndex]);

  const activeDept = activeIndex >= 0 ? departmentData[activeIndex] : null;

  return (
    <section ref={sectionRef} className="relative w-full bg-slate-950 py-16 md:py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="section-header text-center mb-12 md:mb-16">
          <h2 className="font-custom text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4">Departments</h2>
       </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-5">
          {departmentData.map((dept, index) => (
            <DepartmentCard
              key={dept.id}
              dept={dept}
              index={index}
              isActive={index === activeIndex}
              onClick={() => setActiveIndex(index === activeIndex ? -1 : index)}
            />
          ))}
        </div>

        {activeDept && (
          <div ref={detailRef} className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div
                className="md:w-2/5 min-h-[200px] bg-cover bg-center"
                style={{ backgroundImage: `url(${activeDept.image})` }}
              >
                <div className="w-full h-full bg-gradient-to-r from-slate-900/60 to-transparent hidden md:block" />
              </div>
              <div className="md:w-3/5 p-6 md:p-10">
                <h3 className="text-2xl md:text-3xl font-bold text-white font-custom mb-4">{activeDept.title}</h3>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-6 font-custom">
                  {activeDept.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {activeDept.subareas.map((area, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 text-gray-300 border border-white/10 font-custom"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
